export type UploadKind = 'images' | 'quotes' | 'files';

export interface UploadConfig {
  accept: string;
  actionLabel: string;
  description: string;
  formats: string[];
  iconClassName: string;
  kind: UploadKind;
  title: string;
}

export const MAX_LEGACY_QUOTE_SIZE = 100 * 1024 * 1024;
export const MAX_PDF_SIZE = 100 * 1024 * 1024;
const IMAGE_MEDIA_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'mp4', '3gp'];

const getFileExtension = (fileName: string) => fileName.split('.').pop()?.toLowerCase() ?? '';

export const uploadConfigs: Record<UploadKind, UploadConfig> = {
  images: {
    kind: 'images',
    title: 'Subir Imágenes y Videos',
    actionLabel: 'Subir archivos',
    accept: '.jpg,.jpeg,.png,.webp,.gif,.mp4,.3gp',
    description: 'Arrastra y suelta aquí',
    formats: ['JPG', 'PNG', 'WEBP', 'GIF', 'MP4', '3GP', 'Máx. 16 MB'],
    iconClassName: 'bg-brand-light text-brand-vivid',
  },
  quotes: {
    kind: 'quotes',
    title: 'Subir Cotización',
    actionLabel: 'Subir cotización',
    accept: '.pdf',
    description: 'Arrastra y suelta la cotización',
    formats: ['PDF', 'Máx. 100 MB'],
    iconClassName: 'bg-error-light text-error',
  },
  files: {
    kind: 'files',
    title: 'Subir Archivos',
    actionLabel: 'Subir archivos',
    accept: '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.mp4,.3gp,.opus,.ogg,.aac,.amr,.mp3,.m4a',
    description: 'Arrastra y suelta los archivos',
    formats: [
      'PDF',
      'DOC',
      'XLS',
      'PPT',
      'JPG',
      'PNG',
      'MP4',
      'OPUS',
      'OGG',
      'AAC',
      'AMR',
      'MP3',
      'M4A',
      'Máx. 100 MB',
    ],
    iconClassName: 'bg-brand-sky-light text-brand-vivid',
  },
};

export const getUploadError = (kind: UploadKind, files: File[] | null): string | null => {
  if (!files?.length) return 'No hay archivos seleccionados';

  if (kind === 'images') {
    const invalid = files.filter((file) => !IMAGE_MEDIA_EXTENSIONS.includes(getFileExtension(file.name)));
    if (invalid.length > 0) return 'Solo se permiten imágenes JPG, PNG, WEBP, GIF o videos MP4, 3GP';
  }

  if (kind === 'quotes') {
    if (files.some((file) => getFileExtension(file.name) !== 'pdf')) return 'Solo se permiten cotizaciones en PDF';
    if (files.some((file) => file.size > MAX_LEGACY_QUOTE_SIZE)) return 'Hay cotizaciones que pesan más de 100 MB';
  }

  if (kind === 'files') {
    if (files.length === 0) return 'No hay archivos seleccionados';
  }

  return null;
};

interface UploadFeedbackResponse {
  failed: number;
  successful: number;
  errors?: Array<{ fileName: string; error?: string }>;
}

export const formatUploadFeedback = (response: UploadFeedbackResponse): string => {
  if (response.failed > 0 && response.errors?.length) {
    return response.errors.map((error) => `${error.fileName}: ${error.error ?? 'Error desconocido'}`).join(', ');
  }
  const noun = response.successful === 1 ? 'archivo subido' : 'archivos subidos';
  return `${response.successful} ${noun} correctamente`;
};
